import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { HotelDetailURLParams, AllURLParams } from "@/lib/Types/URLParameters";
import { PriceCurveDataItem } from "@/lib/Types/PriceCurveResponse/PriceCurveResponse";
import fetchData from "@/lib/Fetchers/fetchData";
import { sendKibanaLog } from "@/app/api/sendKibanaLog";
import { useReactiveSearchParams } from "@/lib/Hooks/useReactiveSearchParams";
import { getFilterKey } from "@/lib/Helpers/getFilterKey";
import { usePathname } from "next/navigation";
import { viewModeFromPathAndQuery } from "@/lib/Helpers/currentUrl";
import { channel } from "@/lib/Types/Channel";
import { AppConfig } from "@/lib/Types/AppConfig";
import {
  calculatePriceCurveFromDate,
  calculatePriceCurveToDate,
} from "@/lib/Helpers/dateHelpers";

interface PriceCurveParams {
  channel: string;
  cid: string;
  country: string;
  duration: string;
  departureAirport?: string;
  flexibleOffers: boolean;
  from: string;
  region: string;
  app: boolean;
  device: string;
  multiroom: boolean;
  m: number;
  p: number;
  to: string;
  rooms: string;
  travellers: string;
  userUid: string;
  mealType?: string;
  price?: string;
  roomType?: string;
  oceanView?: string;
  transfer?: string;
  railAndFly?: string;
  directFlight?: string;
  tourOperator?: string;
  airlineCodes?: string;
  departureFlightTimeFrom?: string;
  departureFlightTimeUntil?: string;
  returnFlightTimeFrom?: string;
  returnFlightTimeUntil?: string;
  rentalCar?: string;
  categories?: string;
  specialDeals?: boolean;
}

async function fetchPriceCurveClient(
  searchParams: HotelDetailURLParams,
  config: AppConfig,
): Promise<PriceCurveDataItem[]> {
  const { envConfig, viewMode, pathname, userData, mpt } = config;
  const hotelCID = pathname?.match(/cid_(\d+)/) ?? [];

  const paramsObject: PriceCurveParams = {
    channel: viewMode.channel,
    cid: hotelCID[1],
    country: searchParams.country,
    duration: searchParams.duration,
    ...(searchParams.departureAirport && {
      departureAirport: searchParams.departureAirport,
    }),
    from: calculatePriceCurveFromDate(searchParams.from),
    region: searchParams.region,
    app: searchParams.app === "true",
    device: userData?.deviceType ?? "",
    m: viewMode.mandator,
    p: viewMode.profile,
    to: calculatePriceCurveToDate(searchParams.to),
    rooms: searchParams.rooms || "25,25",
    multiroom: (searchParams.rooms || "25,25").split(";").length > 1,
    travellers: (searchParams.rooms || "25,25").replaceAll(";", ","),
    flexibleOffers: searchParams.flexibleOffers === "flexibleCancellation",
    userUid: userData?.userUid || "",

    ...(searchParams.mealType && { mealType: searchParams.mealType }),
    ...(searchParams.price && { price: `0_${searchParams.price}` }),
    ...(searchParams.roomType && { roomType: searchParams.roomType }),
    ...(searchParams.oceanView && { oceanView: searchParams.oceanView }),
    ...(searchParams.transfer && { transfer: searchParams.transfer }),
    ...(["1", "true"].includes(searchParams.railAndFly) && {
      railAndFly: "1",
    }),
    ...(searchParams.directFlight && {
      directFlight: searchParams.directFlight,
    }),
    ...(searchParams.tourOperator && {
      tourOperator: searchParams.tourOperator,
    }),
    ...(searchParams.airline && { airlineCodes: searchParams.airline }),
    ...(searchParams.departureFlightTimeFrom && {
      departureFlightTimeFrom: searchParams.departureFlightTimeFrom,
    }),
    ...(searchParams.departureFlightTimeUntil && {
      departureFlightTimeUntil: searchParams.departureFlightTimeUntil,
    }),
    ...(searchParams.returnFlightTimeFrom && {
      returnFlightTimeFrom: searchParams.returnFlightTimeFrom,
    }),
    ...(searchParams.returnFlightTimeUntil && {
      returnFlightTimeUntil: searchParams.returnFlightTimeUntil,
    }),
    ...(["1", "true"].includes(searchParams.rentalCar) && {
      rentalCar: "1",
    }),
    ...(searchParams.categories && {
      categories: searchParams.categories,
    }),
    ...(searchParams.specialDeals === "true" && {
      specialDeals: true,
    }),
  };

  const url = `${envConfig.api}/comvel-pricecurve-service/rest/pricecurve`;

  sendKibanaLog({
    envConfig,
    transactionId: searchParams?.transactionId,
    message: "PRICE CURVE START",
    component: "priceCurve",
    jsonMessage: paramsObject,
    searchId: searchParams.searchId,
    category: "debug",
  });

  const data = await fetchData({
    url,
    method: "GET",
    params: paramsObject,
    mpt: mpt || undefined,
    envConfig,
    service: "priceCurve",
    transactionId: searchParams?.transactionId,
    searchId: searchParams.searchId,
  });

  sendKibanaLog({
    envConfig,
    transactionId: searchParams?.transactionId,
    message: `PRICE CURVE ${data?.apiError ? "FAIL" : "SUCCESS"}`,
    component: "priceCurve",
    jsonMessage: { message: data?.apiError && data.message },
    searchId: searchParams.searchId,
    category: "debug",
  });

  if (data?.apiError) {
    throw new Error(data.message || "priceCurve: fetch failed");
  }

  return data;
}

export function usePriceCurve(appConfig: AppConfig) {
  const { envConfig, WlConfig } = appConfig;
  const searchParams = useReactiveSearchParams();
  const pathname = usePathname();

  const searchParamsObject = useMemo(() => {
    const params: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params as HotelDetailURLParams;
  }, [searchParams]);

  const viewMode = viewModeFromPathAndQuery(
    pathname,
    searchParamsObject as AllURLParams,
    WlConfig.viewMode,
  );

  const filterKey = useMemo(
    () =>
      getFilterKey(searchParamsObject, [
        "searchId",
        "preselectedOfferId",
        "clickId",
        "priceCurveFrom",
        "sort",
      ]),
    [searchParamsObject],
  );

  const enabled =
    !!envConfig &&
    !!viewMode &&
    viewMode.channel !== channel.HOTEL &&
    !!searchParamsObject.duration &&
    searchParamsObject.duration !== "0" &&
    !!searchParamsObject.country &&
    !!searchParamsObject.region;

  const query = useQuery({
    queryKey: ["priceCurve", pathname, viewMode?.channel, filterKey],
    queryFn: () =>
      fetchPriceCurveClient(searchParamsObject, {
        ...appConfig,
        pathname,
        viewMode,
      }),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled,
    retry: 2,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    enabled,
  };
}
